import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Save, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, erroApi } from './api';

export default function Identidade() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [subindo, setSubindo] = useState(false);
  const [previa, setPrevia] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-identidade'],
    queryFn: async () => (await apiFetch('/admin/identidade')).json(),
  });

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ['admin-identidade'] });
    qc.invalidateQueries({ queryKey: ['identidade'] });
  };

  const enviar = async (lista) => {
    if (!lista?.length) return;
    setSubindo(true);
    const fd = new FormData();
    fd.append('files', lista[0]);
    const r = await apiFetch('/admin/upload?tipo=logo', { method: 'POST', body: fd });
    setSubindo(false);
    if (r.ok) {
      const d = await r.json();
      setPrevia(d.files[0].url);
      toast.success('Imagem enviada. Confira a prévia e salve.');
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const salvar = async () => {
    if (!previa) return;
    const r = await apiFetch('/admin/identidade', { method: 'PUT', body: JSON.stringify({ logoUrl: previa }) });
    if (r.ok) {
      toast.success('Logo atualizada no catálogo!');
      setPrevia(null);
      invalidar();
    } else {
      toast.error('Não foi possível salvar a logo.');
    }
  };

  const restaurar = async () => {
    const r = await apiFetch('/admin/identidade/restaurar', { method: 'POST' });
    if (r.ok) {
      toast.success('Logo anterior restaurada.');
      invalidar();
    } else {
      toast.error(erroApi((await r.json().catch(() => ({}))).detail));
    }
  };

  if (isLoading) return <p className="text-sm text-slate-400">Carregando...</p>;

  return (
    <div data-testid="admin-identidade" className="max-w-xl">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Logo e identidade visual</h1>
      <p className="mt-1 text-sm text-slate-500">Troque a logo em destaque no catálogo sem editar o código. PNG com fundo transparente funciona melhor.</p>

      <div className="mt-5 rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Logo atual do catálogo</p>
        <div className="mt-3 flex items-center gap-4">
          <span className="flex h-24 w-24 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-2">
            <img data-testid="logo-atual" src={data.logoUrl} alt="Logo atual" className="max-h-full max-w-full object-contain" />
          </span>
          <div className="text-sm text-slate-500">
            <p>Exibida no cabeçalho e no rodapé do catálogo.</p>
            {data.logoAnterior && (
              <button data-testid="logo-restaurar" onClick={restaurar} className="mt-2 inline-flex items-center gap-1.5 rounded-full border-2 border-slate-300 px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50">
                <RotateCcw size={13} /> Restaurar logo anterior
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 rounded-2xl bg-white p-5 shadow-sm">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Nova logo</p>
        <button
          data-testid="logo-upload-button"
          disabled={subindo}
          onClick={() => fileRef.current?.click()}
          className="mt-3 inline-flex items-center gap-2 rounded-full bg-mk-blue px-5 py-2.5 text-sm font-bold text-white hover:bg-mk-bluelight disabled:opacity-60"
        >
          <Upload size={15} /> {subindo ? 'Enviando...' : 'Enviar imagem (PNG, JPG ou WebP)'}
        </button>
        <input ref={fileRef} data-testid="logo-file-input" type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => enviar(e.target.files)} />
        {previa && (
          <div className="mt-4 rounded-2xl border border-dashed border-mk-blue/40 bg-blue-50/50 p-4">
            <p className="text-xs font-bold uppercase tracking-wide text-mk-blue">Prévia da nova logo</p>
            <div className="mt-3 flex items-center gap-4">
              <span className="flex h-24 w-24 items-center justify-center rounded-2xl border border-slate-200 bg-white p-2">
                <img data-testid="logo-previa" src={previa} alt="Prévia da nova logo" className="max-h-full max-w-full object-contain" />
              </span>
              <div className="flex flex-col gap-2">
                <button data-testid="logo-save-button" onClick={salvar} className="inline-flex items-center gap-2 rounded-full bg-mk-red px-5 py-2.5 text-sm font-bold text-white hover:bg-mk-redbright">
                  <Save size={15} /> Salvar nova logo
                </button>
                <button onClick={() => setPrevia(null)} className="rounded-full px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100">
                  Descartar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
